import { useEffect, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import {
  adminGA,
  type GAOverview,
  type GAPage,
  type GASource,
  type GADevice,
  type GAGeoItem,
  type GADay,
  type GAStatus,
} from '../../api/admin';
import {
  Globe, Users, Eye, Clock, ArrowUpRight, Monitor, Smartphone, Tablet,
  MapPin, Search, FileText, Zap, TrendingUp, TrendingDown, BarChart3,
  RefreshCw, ExternalLink, Activity, Link2, Shield, Copy, Check,
  Calendar,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend,
} from 'recharts';

// ── Date range presets ───────────────────────────────────
// "All time" pinned at 730 days. GA4's default 14-month retention
// means anything older returns nothing anyway; the 2-year floor just
// avoids API errors from arbitrarily old start dates.
type RangePreset = '24h' | '7d' | '30d' | '3m' | '1y' | 'all' | 'custom';

const PRESET_LABELS: Record<Exclude<RangePreset, 'custom'>, string> = {
  '24h': 'Last 24h',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '3m': 'Last 3 months',
  '1y': 'Last 1 year',
  'all': 'All time',
};

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function shiftDays(iso: string, n: number): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function rangeFor(preset: RangePreset, custom?: { from: string; to: string }): { from: string; to: string } {
  const today = todayISO();
  switch (preset) {
    case '24h': return { from: today, to: today };
    case '7d':  return { from: shiftDays(today, -6), to: today };
    case '30d': return { from: shiftDays(today, -29), to: today };
    case '3m':  return { from: shiftDays(today, -89), to: today };
    case '1y':  return { from: shiftDays(today, -364), to: today };
    case 'all': return { from: shiftDays(today, -729), to: today };
    case 'custom':
      return custom && custom.from && custom.to ? custom : { from: shiftDays(today, -29), to: today };
  }
}

const GA_PROPERTY_ID = import.meta.env.VITE_GA_MEASUREMENT_ID ?? '';
const gaUrl = (path: string) =>
  `https://analytics.google.com/analytics/web/#/p${GA_PROPERTY_ID}/${path}`;

function pctChange(curr: number, prev: number): { pct: number; dir: 'up' | 'down' | 'flat' } {
  if (prev === 0) return { pct: curr > 0 ? 100 : 0, dir: curr > 0 ? 'up' : 'flat' };
  const pct = Math.round(((curr - prev) / prev) * 100);
  return { pct: Math.abs(pct), dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' };
}

function fmtDuration(s: number): string {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${sec}s`;
}

function fmtDate(d: string): string {
  if (d.length === 8) {
    return new Date(`${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  }
  return d;
}

export default function GoogleAnalyticsPage() {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<GAOverview | null>(null);
  const [pages, setPages] = useState<GAPage[]>([]);
  const [sources, setSources] = useState<GASource[]>([]);
  const [devices, setDevices] = useState<GADevice[]>([]);
  const [countries, setCountries] = useState<GAGeoItem[]>([]);
  const [cities, setCities] = useState<GAGeoItem[]>([]);
  const [days, setDays] = useState<GADay[]>([]);
  const [status, setStatus] = useState<GAStatus | null>(null);
  const [error, setError] = useState('');
  const [connectErr, setConnectErr] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  // Set by the header's Reconnect button. Forces the setup walkthrough
  // to re-render even when env is fully set, so the admin can review
  // OAuth client / redirect URI before starting a fresh consent flow.
  const [forceSetup, setForceSetup] = useState(false);
  const [preset, setPreset] = useState<RangePreset>('30d');
  const [customRange, setCustomRange] = useState<{ from: string; to: string }>(() => {
    const today = todayISO();
    return { from: shiftDays(today, -29), to: today };
  });

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const st = await adminGA.status();
      setStatus(st);
      const fullyConfigured = st.has_client_id && st.has_client_secret && st.has_refresh_token && st.has_property_id;
      if (!fullyConfigured) {
        // Still call overview so older code paths/tests see configured=false; cheaper than 6 calls.
        const ov = await adminGA.overview();
        setOverview(ov);
        return;
      }
      const range = rangeFor(preset, customRange);
      const [ov, pg, src, dev, geo, trend] = await Promise.all([
        adminGA.overview(range),
        adminGA.topPages(range),
        adminGA.trafficSources(range),
        adminGA.devices(range),
        adminGA.geo(range),
        adminGA.dailyTrend(range),
      ]);
      setOverview(ov);
      setPages(pg.pages || []);
      setSources(src.sources || []);
      setDevices(dev.devices || []);
      setCountries(geo.countries || []);
      setCities(geo.cities || []);
      setDays(trend.days || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when the date range changes. customRange only matters
  // while preset === 'custom', otherwise its value is ignored.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, [preset, customRange.from, customRange.to]);

  const startConnect = async () => {
    setConnectErr('');
    try {
      const { url } = await adminGA.oauthUrl();
      window.location.href = url;
    } catch (err) {
      setConnectErr(err instanceof Error ? err.message : 'Failed to start OAuth');
    }
  };

  const copy = (text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    });
  };

  const card = `rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`;
  const headSm = `text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`;
  const cellCls = `px-3 py-2.5 text-sm ${isDark ? 'border-gray-800' : 'border-gray-100'}`;

  // Unconfigured state: render setup walkthrough with Connect button.
  // Three stages — none of these need a server-side service account
  // (org policy blocks SA keys), all go through the OAuth refresh-token flow.
  const fullyConfigured =
    !!status && status.has_client_id && status.has_client_secret && status.has_refresh_token && status.has_property_id;

  if (!loading && (!fullyConfigured || forceSetup)) {
    // forceSetup (Reconnect click) re-shows the env walkthrough even
    // when env is already complete, so the admin can verify the OAuth
    // client / redirect URI before kicking off a new consent.
    const stage: 'env' | 'connect' = forceSetup
      ? 'env'
      : (!status?.has_client_id || !status?.has_client_secret || !status?.has_property_id ? 'env' : 'connect');
    const envFullySet = !!status?.has_client_id && !!status?.has_client_secret && !!status?.has_property_id;

    const codeBox = `inline-block w-full text-left rounded-lg font-mono text-xs px-4 py-3 ${isDark ? 'bg-gray-950 text-emerald-300 border border-gray-800' : 'bg-gray-900 text-emerald-300'}`;
    const stepBadge = (done: boolean) =>
      `flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${done ? 'bg-emerald-500 text-white' : isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-600'}`;

    const checkmark = (ok: boolean) => ok
      ? <span className="text-emerald-500 text-xs font-semibold">✓ set</span>
      : <span className={`text-xs font-semibold ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>not set</span>;

    return (
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe size={24} /> Google Analytics
          </h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            One-time OAuth setup. No service account needed — uses a refresh token in env (same pattern as Gmail).
          </p>
        </div>

        {/* Current env status checklist */}
        <div className={card}>
          <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
            <Shield size={14} className="text-emerald-500" /> Env vars
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><code className="font-mono text-xs">GA_PROPERTY_ID</code> {checkmark(!!status?.has_property_id)}</div>
            <div className="flex justify-between"><code className="font-mono text-xs">GA_OAUTH_CLIENT_ID</code> {checkmark(!!status?.has_client_id)}</div>
            <div className="flex justify-between"><code className="font-mono text-xs">GA_OAUTH_CLIENT_SECRET</code> {checkmark(!!status?.has_client_secret)}</div>
            <div className="flex justify-between"><code className="font-mono text-xs">GA_REFRESH_TOKEN</code> {checkmark(!!status?.has_refresh_token)}</div>
          </div>
        </div>

        {stage === 'env' ? (
          // STAGE 1: GCP console steps. Show the redirect URI prominently
          // because that's the field people get wrong most often.
          <div className={card}>
            <div className="flex items-start gap-3 mb-4">
              <div className={stepBadge(false)}>1</div>
              <div>
                <h3 className="font-semibold mb-1">Create the OAuth client in Google Cloud</h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  This creates an OAuth client (allowed under org policies), not a service account.
                </p>
              </div>
            </div>

            <ol className={`space-y-3 text-sm pl-10 list-decimal ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <li>
                Open <a className="text-emerald-500 hover:underline" href="https://console.cloud.google.com/apis/library/analyticsdata.googleapis.com" target="_blank" rel="noopener noreferrer">Google Cloud Console → Analytics Data API</a> and click <strong>Enable</strong>.
              </li>
              <li>
                Go to <a className="text-emerald-500 hover:underline" href="https://console.cloud.google.com/apis/credentials/consent" target="_blank" rel="noopener noreferrer">OAuth consent screen</a>, choose <strong>External</strong>, fill in app name + your email, save.
                Under <strong>Test users</strong> add the Google account that owns your GA4 property.
              </li>
              <li>
                Go to <a className="text-emerald-500 hover:underline" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">Credentials</a> → <strong>+ Create Credentials</strong> → <strong>OAuth client ID</strong> → <strong>Web application</strong>.
              </li>
              <li>
                Under <strong>Authorized redirect URIs</strong>, paste exactly this:
                <div className="flex items-center gap-2 mt-2">
                  <div className={codeBox + ' flex-1'}>{status?.redirect_uri ?? '(loading)'}</div>
                  <button
                    onClick={() => status?.redirect_uri && copy(status.redirect_uri, 'redirect')}
                    className={`p-2 rounded-lg ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}
                    title="Copy"
                  >
                    {copiedField === 'redirect' ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                  </button>
                </div>
                <p className={`text-xs mt-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Must match exactly. If you develop locally too, add <code>http://localhost:{`{PORT}`}/api/admin/ga/oauth/callback</code> as a second URI.
                </p>
              </li>
              <li>
                Click <strong>Create</strong> → copy the <strong>Client ID</strong> and <strong>Client secret</strong> shown.
              </li>
              <li>
                Also grab your GA4 <strong>Property ID</strong> from <a className="text-emerald-500 hover:underline" href="https://analytics.google.com" target="_blank" rel="noopener noreferrer">Google Analytics → Admin → Property details</a> (numeric, not the G-XXX one).
              </li>
            </ol>

            <div className="flex items-start gap-3 mt-6 mb-3">
              <div className={stepBadge(false)}>2</div>
              <div>
                <h3 className="font-semibold mb-1">Add three env vars and restart the server</h3>
              </div>
            </div>
            <div className={codeBox + ' ml-10'}>
              <div>GA_PROPERTY_ID=123456789</div>
              <div>GA_OAUTH_CLIENT_ID=...apps.googleusercontent.com</div>
              <div>GA_OAUTH_CLIENT_SECRET=GOCSPX-...</div>
            </div>
            <p className={`text-xs mt-2 ml-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Add the same vars to your Hostinger env panel for production. <code>GA_REFRESH_TOKEN</code> is filled in by step 3 after you reload this page.
            </p>

            {connectErr && (
              <div className={`mt-6 ml-10 rounded-lg p-3 text-sm ${isDark ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-red-50 border border-red-200 text-red-600'}`}>
                {connectErr}
              </div>
            )}

            <div className="mt-6 flex items-center gap-3 ml-10 flex-wrap">
              {envFullySet ? (
                // Reconnect path: env is already set, so the user is here
                // to re-do the consent flow with a different Google account.
                <button
                  onClick={startConnect}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  <Link2 size={14} /> Connect GA4
                </button>
              ) : (
                <button
                  onClick={fetchAll}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  <RefreshCw size={14} /> I've added the env vars — reload
                </button>
              )}
              {forceSetup && fullyConfigured && (
                <button
                  onClick={() => setForceSetup(false)}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        ) : (
          // STAGE 2: env is set, ready to do the OAuth dance.
          <div className={card}>
            <div className="flex items-start gap-3 mb-4">
              <div className={stepBadge(false)}>3</div>
              <div>
                <h3 className="font-semibold mb-1">Connect to Google Analytics</h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Click below, sign in with the Google account that has Viewer access to your GA4 property, and click <strong>Allow</strong>. The next page shows your <code>GA_REFRESH_TOKEN</code> — paste it into env and restart.
                </p>
              </div>
            </div>

            {connectErr && (
              <div className={`mb-4 rounded-lg p-3 text-sm ${isDark ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-red-50 border border-red-200 text-red-600'}`}>
                {connectErr}
              </div>
            )}

            <button
              onClick={startConnect}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <Link2 size={16} /> Connect GA4
            </button>
            <p className={`text-xs mt-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              You'll be redirected to Google. Don't worry if Google warns you the app is "unverified" — that's expected for a self-built OAuth client. Click <em>Advanced</em> → <em>Continue</em>.
            </p>
          </div>
        )}

        <div className={`rounded-xl border p-4 text-xs ${isDark ? 'bg-gray-900 border-gray-800 text-gray-400' : 'bg-blue-50 border-blue-200 text-blue-900'}`}>
          <strong>Switching the source Google account later?</strong> Once the page is showing data, you'll see a <em>Reconnect</em> link in the header. Click it, sign in with the new account, replace <code>GA_REFRESH_TOKEN</code> in env with the new value, and restart. The OAuth client itself (id + secret) doesn't change.
        </div>
      </div>
    );
  }

  const m = overview?.metrics;

  const metricCards = m ? [
    { label: 'Active Users', value: m.active_users.value.toLocaleString(), prev: m.active_users.prev, curr: m.active_users.value, icon: Users, color: 'emerald' },
    { label: 'Sessions', value: m.sessions.value.toLocaleString(), prev: m.sessions.prev, curr: m.sessions.value, icon: Activity, color: 'blue' },
    { label: 'Page Views', value: m.page_views.value.toLocaleString(), prev: m.page_views.prev, curr: m.page_views.value, icon: Eye, color: 'purple' },
    { label: 'Avg. Duration', value: fmtDuration(m.avg_session_duration.value), prev: m.avg_session_duration.prev, curr: m.avg_session_duration.value, icon: Clock, color: 'amber' },
    { label: 'Bounce Rate', value: `${m.bounce_rate.value}%`, prev: m.bounce_rate.prev, curr: m.bounce_rate.value, icon: ArrowUpRight, color: 'red', invert: true },
    { label: 'New Users', value: m.new_users.value.toLocaleString(), prev: m.new_users.prev, curr: m.new_users.value, icon: Users, color: 'sky' },
  ] : [];

  const deviceTotal = devices.reduce((s, d) => s + d.sessions, 0) || 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe size={24} /> Google Analytics
          </h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {preset === 'custom' ? `${customRange.from} → ${customRange.to}` : PRESET_LABELS[preset]} &middot; live data from GA4 API
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => { setForceSetup(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            title="Re-open the setup walkthrough to swap GA4 source account"
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>
            <Link2 size={12} /> Reconnect
          </button>
          <button onClick={fetchAll} disabled={loading}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} ${loading ? 'opacity-50' : ''}`}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <a href={gaUrl('reports/reportinghub')} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors">
            Full GA4 <ExternalLink size={14} />
          </a>
        </div>
      </div>

      {error && (
        <div className={`rounded-xl border p-4 text-sm ${isDark ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>
          {error}
        </div>
      )}

      {/* Date range selector */}
      <div className={`rounded-xl border p-3 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar size={14} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
          {(Object.keys(PRESET_LABELS) as Array<keyof typeof PRESET_LABELS>).map(p => {
            const active = preset === p;
            return (
              <button
                key={p}
                onClick={() => setPreset(p)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  active
                    ? 'bg-emerald-600 text-white'
                    : isDark
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {PRESET_LABELS[p]}
              </button>
            );
          })}
          <button
            onClick={() => setPreset('custom')}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
              preset === 'custom'
                ? 'bg-emerald-600 text-white'
                : isDark
                  ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Custom
          </button>
        </div>
        {preset === 'custom' && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <label className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              From
              <input
                type="date"
                value={customRange.from}
                max={customRange.to || todayISO()}
                onChange={e => setCustomRange(r => ({ ...r, from: e.target.value }))}
                className={`ml-2 px-2 py-1 text-xs rounded border ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300'}`}
              />
            </label>
            <label className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              To
              <input
                type="date"
                value={customRange.to}
                min={customRange.from}
                max={todayISO()}
                onChange={e => setCustomRange(r => ({ ...r, to: e.target.value }))}
                className={`ml-2 px-2 py-1 text-xs rounded border ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300'}`}
              />
            </label>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {/* Metric cards */}
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {metricCards.map(mc => {
              const { pct, dir } = pctChange(mc.curr, mc.prev);
              const isGood = mc.invert ? dir === 'down' : dir === 'up';
              return (
                <div key={mc.label} className={card}>
                  <div className="flex items-center justify-between mb-3">
                    <mc.icon size={18} className={`text-${mc.color}-500`} />
                    {dir !== 'flat' && (
                      <span className={`flex items-center gap-0.5 text-[10px] font-bold ${isGood ? 'text-emerald-500' : 'text-red-400'}`}>
                        {isGood ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                        {pct}%
                      </span>
                    )}
                  </div>
                  <p className="text-xl font-bold">{mc.value}</p>
                  <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{mc.label}</p>
                </div>
              );
            })}
          </div>

          {/* Daily trend — sessions + users line chart */}
          <div className={card}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <BarChart3 size={16} className="text-emerald-500" /> Sessions &amp; Users ({preset === 'custom' ? 'Custom range' : PRESET_LABELS[preset]})
              </h3>
            </div>
            {days.length === 0 ? (
              <div className={`flex flex-col items-center justify-center h-64 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                <p>No daily data for this range.</p>
                <p className="text-xs mt-1">GA4 has a 24–48h delay on daily aggregates — try a longer range, or check back tomorrow.</p>
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={days.map(d => ({ label: fmtDate(d.date), sessions: d.sessions, users: d.users }))} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1f2937' : '#e5e7eb'} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: isDark ? '#6b7280' : '#9ca3af' }}
                      interval="preserveStartEnd"
                      minTickGap={20}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: isDark ? '#6b7280' : '#9ca3af' }}
                      allowDecimals={false}
                    />
                    <RTooltip
                      contentStyle={{
                        backgroundColor: isDark ? '#111827' : '#fff',
                        border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                        borderRadius: '8px', fontSize: 12,
                      }}
                    />
                    <Legend iconSize={10} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                    <Line type="monotone" dataKey="sessions" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} name="Sessions" />
                    <Line type="monotone" dataKey="users" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} name="Users" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Traffic Sources + Devices */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Traffic Sources */}
            <div className={`${card} lg:col-span-2`}>
              <h3 className="font-semibold flex items-center gap-2 mb-4">
                <TrendingUp size={16} className="text-blue-500" /> Traffic Sources
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className={isDark ? 'border-b border-gray-800' : 'border-b border-gray-100'}>
                      <th className={`${headSm} pb-2`}>Source / Medium</th>
                      <th className={`${headSm} pb-2 text-right`}>Sessions</th>
                      <th className={`${headSm} pb-2 text-right`}>Users</th>
                      <th className={`${headSm} pb-2 text-right`}>Bounce</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map((s, i) => (
                      <tr key={i} className={isDark ? 'border-b border-gray-800/50' : 'border-b border-gray-50'}>
                        <td className={cellCls}>
                          <span className="font-medium">{s.source}</span>
                          <span className={`text-xs ml-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>/ {s.medium}</span>
                        </td>
                        <td className={`${cellCls} text-right font-semibold`}>{s.sessions.toLocaleString()}</td>
                        <td className={`${cellCls} text-right`}>{s.users.toLocaleString()}</td>
                        <td className={`${cellCls} text-right`}>{s.bounce_rate}%</td>
                      </tr>
                    ))}
                    {sources.length === 0 && (
                      <tr><td colSpan={4} className={`${cellCls} text-center ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>No data</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Devices */}
            <div className={card}>
              <h3 className="font-semibold flex items-center gap-2 mb-4">
                <Monitor size={16} className="text-purple-500" /> Devices
              </h3>
              <div className="space-y-4">
                {devices.map(d => {
                  const pct = Math.round((d.sessions / deviceTotal) * 100);
                  const Icon = d.device === 'mobile' ? Smartphone : d.device === 'tablet' ? Tablet : Monitor;
                  return (
                    <div key={d.device}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="flex items-center gap-2 text-sm font-medium capitalize">
                          <Icon size={14} /> {d.device}
                        </span>
                        <span className={`text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{pct}%</span>
                      </div>
                      <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                        <div className="h-full rounded-full bg-purple-500 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{d.sessions.toLocaleString()} sessions &middot; {d.users.toLocaleString()} users</p>
                    </div>
                  );
                })}
                {devices.length === 0 && (
                  <p className={`text-sm text-center py-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>No data</p>
                )}
              </div>
            </div>
          </div>

          {/* Top Pages + Geography */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Pages */}
            <div className={card}>
              <h3 className="font-semibold flex items-center gap-2 mb-4">
                <FileText size={16} className="text-emerald-500" /> Top Pages
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className={isDark ? 'border-b border-gray-800' : 'border-b border-gray-100'}>
                      <th className={`${headSm} pb-2`}>Page</th>
                      <th className={`${headSm} pb-2 text-right`}>Views</th>
                      <th className={`${headSm} pb-2 text-right`}>Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pages.slice(0, 10).map((p, i) => (
                      <tr key={i} className={isDark ? 'border-b border-gray-800/50' : 'border-b border-gray-50'}>
                        <td className={`${cellCls} truncate max-w-[200px] font-mono text-xs`}>{p.path}</td>
                        <td className={`${cellCls} text-right font-semibold`}>{p.views.toLocaleString()}</td>
                        <td className={`${cellCls} text-right`}>{p.users.toLocaleString()}</td>
                      </tr>
                    ))}
                    {pages.length === 0 && (
                      <tr><td colSpan={3} className={`${cellCls} text-center ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>No data</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Geography */}
            <div className={card}>
              <h3 className="font-semibold flex items-center gap-2 mb-4">
                <MapPin size={16} className="text-sky-500" /> Geography
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className={`${headSm} mb-2`}>Countries</p>
                  <div className="space-y-1.5">
                    {countries.slice(0, 8).map((c, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span>{c.name}</span>
                        <span className="font-semibold">{c.users.toLocaleString()}</span>
                      </div>
                    ))}
                    {countries.length === 0 && <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>No data</p>}
                  </div>
                </div>
                <div>
                  <p className={`${headSm} mb-2`}>Cities</p>
                  <div className="space-y-1.5">
                    {cities.slice(0, 8).map((c, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="truncate">{c.name}</span>
                        <span className="font-semibold">{c.users.toLocaleString()}</span>
                      </div>
                    ))}
                    {cities.length === 0 && <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>No data</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Google Webmaster Quick Links */}
          <div className={card}>
            <h3 className="font-semibold text-sm mb-4">Google Webmaster Tools</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Search Console', href: 'https://search.google.com/search-console?resource_id=sc-domain:zerosmoke.in', icon: Search, desc: 'Search performance' },
                { label: 'PageSpeed Insights', href: 'https://pagespeed.web.dev/analysis?url=https://zerosmoke.in', icon: Zap, desc: 'Site speed scores' },
                { label: 'Tag Manager', href: 'https://tagmanager.google.com', icon: Monitor, desc: 'Tracking tags' },
                { label: 'Rich Results Test', href: 'https://search.google.com/test/rich-results?url=https://zerosmoke.in', icon: FileText, desc: 'Schema check' },
              ].map(link => (
                <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer"
                  className={`group flex items-center gap-3 p-3.5 rounded-lg border transition-all hover:shadow-sm ${isDark ? 'border-gray-800 hover:border-gray-700' : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'}`}>
                  <link.icon size={16} className="text-emerald-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm font-medium block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{link.label}</span>
                    <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{link.desc}</span>
                  </div>
                  <ExternalLink size={12} className={`opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                </a>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
